// src/pages/ActivoDetail.jsx
import React, { useState, useEffect, useCallback } from 'react'
import ActivosForm from './ActivosForm'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Menu, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { HardDrive, Edit2, Cpu, Monitor, Smartphone, Server, Calendar, Building, Mail, Shield, CheckCircle, Tag, QrCode, DollarSign } from 'lucide-react';
import { User, ArrowLeft, MapPin, FileText, History, Activity, Plus, X, MessageSquare, RotateCcw, Download, Phone, Check, Box } from '../components/icons';
import api, { asignacionesAPI } from '../services/api'
import SoftwareSecurityManager from '../components/SoftwareSecurityManager'
import VicariusActivoPanel from '../components/VicariusActivoPanel'
import IntuneBitLockerPanel from '../components/IntuneBitLockerPanel'
import { formatUbicacion } from '../utils/locationUtils'
import EmpresaBadge from '../components/EmpresaBadge'
import RemoteConnectionManager from '../components/RemoteConnectionManager'
import AsignarActivoModal from '../components/AsignarActivoModal'
import HistorialAsignacionesContent from '../components/HistorialAsignacionesContent'
import ActivoQrModal from '../components/ActivoQrModal'
import { useNotificationContext } from '../contexts/NotificationContext'

export default function ActivoDetail() {
    const [editando, setEditando] = useState(false)
    const { id: codigo } = useParams()
    const navigate = useNavigate()
    const [activo, setActivo] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [searchParams, setSearchParams] = useSearchParams()
    const activeTab = searchParams.get('tab') || 'info'
    // la pestaña vive en la URL: al volver a la página se recupera donde estabas
    const setActiveTab = useCallback((id) => {
        setSearchParams((p) => { const n = new URLSearchParams(p); n.set('tab', id); return n }, { replace: true })
    }, [setSearchParams])
    const [showAsignarModal, setShowAsignarModal] = useState(false)
    const [estadoIntune, setEstadoIntune] = useState(null) // 'ok' cuando Intune entrega el cifrado: se oculta el BitLocker manual
    const [showQrModal, setShowQrModal] = useState(false)
    const [isEditingObservaciones, setIsEditingObservaciones] = useState(false)
    const [editObservacionesText, setEditObservacionesText] = useState('')
    const { alertSuccess, alertError } = useNotificationContext()

    const handleSaveObservaciones = async () => {
        try {
            await api.put(`/activos/${activo.codigo}`, { observaciones: editObservacionesText })
            alertSuccess('Observaciones actualizadas correctamente')
            setIsEditingObservaciones(false)
            fetchActivo()
        } catch (error) {
            console.error('Error al actualizar observaciones:', error)
            alertError('Error al actualizar observaciones')
        }
    }

    const fetchActivo = useCallback(async () => {
        if (!codigo) {
            setError('Código de activo no válido')
            setLoading(false)
            return
        }

        try {
            setLoading(true)
            const response = await api.get(`/activos/${codigo}`)


            setActivo(response.data)
        } catch (err) {
            console.error('Error al cargar activo:', err)
            setError('Error al cargar el activo')
        } finally {
            setLoading(false)
        }
    }, [codigo])

    useEffect(() => {
        fetchActivo()
    }, [codigo, fetchActivo])

    const handleAsignacionCreada = () => {
        fetchActivo() // Recargar el activo para mostrar la nueva asignación
    }

    const handleUpdateEstado = async (nuevoEstado) => {
        try {
            await api.put(`/activos/${activo.codigo}`, { estado: nuevoEstado });
            alertSuccess(`Estado actualizado a ${nuevoEstado}`);
            fetchActivo(); // Recargar datos
        } catch (error) {
            console.error('Error al actualizar estado:', error);
            alertError('Error al actualizar el estado del activo');
        }
    };


    const handleQuitarAsignacion = async () => {
        if (!activo.asignadoA || !activo.asignadoA.asignacionId) {
            console.error('No hay asignación activa para quitar');
            return;
        }

        if (window.confirm('¿Estás seguro de que quieres quitar la asignación de este activo? El registro se mantendrá en el historial como Retirada.')) {
            try {
                // En lugar de borrar físicamente (api.delete), usamos devolver para cambiar el estado y preservar el historial
                await asignacionesAPI.devolver(activo.asignadoA.asignacionId, { observaciones: 'Asignación quitada' });
                alertSuccess('Asignación quitada exitosamente');
                fetchActivo(); // Recargar el activo
            } catch (error) {
                console.error('Error al quitar asignación:', error);
                alertError('Error al quitar la asignación');
            }
        }
    }

    const getStatusColor = (estado) => {
        switch (estado?.toLowerCase()) {
            case 'activo':
            case 'asignado':
                return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 border-green-200 dark:border-green-800';
            case 'en mantenimiento':
            case 'mantenimiento':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800';
            case 'dado de baja':
            case 'dada de baja':
            case 'baja':
            case 'retirado':
                return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 border-red-200 dark:border-red-800';
            case 'disponible':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 border-blue-200 dark:border-blue-800';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
        }
    }

    const getCategoryIcon = (categoria) => {
        switch (categoria?.toLowerCase()) {
            case 'equipos':
            case 'computador':
            case 'laptop':
                return <Cpu className="h-5 w-5" />;
            case 'monitores':
            case 'pantalla':
                return <Monitor className="h-5 w-5" />;
            case 'móviles':
            case 'celular':
            case 'smartphone':
                return <Smartphone className="h-5 w-5" />;
            case 'servidores':
            case 'server':
                return <Server className="h-5 w-5" />;
            default:
                return <HardDrive className="h-5 w-5" />;
        }
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'Sin fecha';
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? 'Fecha inválida' : date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    const formatStorageDisplay = (almacenamiento, capacidad, discosJson) => {
        // 1. Prioridad: DiscosJson (Notebooks/Desktops)
        if (discosJson) {
            try {
                const discos = JSON.parse(discosJson);
                if (Array.isArray(discos) && discos.length > 0) {
                    const d = discos[0];
                    const cap = d.capacidad ? (d.capacidad >= 1024 ? `${(d.capacidad / 1024).toFixed(0)}TB` : `${d.capacidad}GB`) : '';
                    const tipo = d.tipo || '';
                    return `${cap} ${tipo}`.trim();
                }
            } catch (e) {
                console.error("Error parsing discosJson:", e);
            }
        }

        // 2. Fallbacks: almacenamiento o capacidad
        const val = almacenamiento || capacidad;
        if (!val) return 'N/A';

        // Evitar duplicación de GB si ya lo trae
        if (typeof val === 'string' && /GB|TB/i.test(val)) {
            return val;
        }
        return `${val}GB`;
    }

    if (loading) {
        return (
            <div className="p-6">
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
                    <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <HardDrive className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Error al cargar activo</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error}</p>
                </div>
            </div>
        );
    }

    if (!activo) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <HardDrive className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Activo no encontrado</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">El activo que buscas no existe o ha sido eliminado.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div className="flex items-center space-x-4">
                        <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-blue-600 flex items-center justify-center">
                            {getCategoryIcon(activo.categoria)}
                        </div>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2 truncate">
                                <span>{activo.nombreEquipo || activo.codigo}</span>
                                <EmpresaBadge empresa={activo.empresa} />
                            </h1>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{activo.codigo}</p>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <button
                        onClick={() => setShowQrModal(true)}
                        className="flex items-center justify-center space-x-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 px-4 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm"
                        title="Generar código QR del activo"
                    >
                        <QrCode className="h-4 w-4" />
                        <span>QR</span>
                    </button>
                    <button
                        onClick={() => setEditando(true)}
                        className="flex-1 sm:flex-none flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm"
                    >
                        <Edit2 className="h-4 w-4" />
                        <span>Editar activo</span>
                    </button>
                </div>
            </div>

            {showQrModal && (
                <ActivoQrModal activo={activo} onClose={() => setShowQrModal(false)} />
            )}

            {/* Status Badge with Quick Edit */}
            <div className="flex items-center space-x-3">
                <div className="relative">
                    {activo.asignadoA ? (
                        <span className="px-3 py-1.5 rounded-full text-sm font-bold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800 shadow-sm flex items-center space-x-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                            <span>Asignado</span>
                        </span>
                    ) : (
                        <span className={`px-3 py-1.5 rounded-full text-sm font-bold flex items-center space-x-2 border shadow-sm ${getStatusColor(activo.estado)}`}>
                            {activo.estado === 'Disponible' && <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>}
                            {activo.estado === 'Activo' && <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>}
                            <span>{activo.estado}</span>
                        </span>
                    )}
                </div>

                <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button className="p-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:shadow-md transition-all duration-200 focus:outline-none">
                        <Edit2 className="h-4 w-4" />
                    </Menu.Button>

                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <Menu.Items className="absolute left-0 mt-2 w-56 origin-top-left bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 ring-1 ring-black ring-opacity-5 focus:outline-none z-50 overflow-hidden">
                            <div className="p-1 text-xs font-bold text-gray-400 uppercase px-3 py-2">Cambiar estado</div>
                            <div className="p-1">
                                {['Disponible', 'Activo', 'En Mantenimiento', 'Retirado', 'Dado de Baja'].map((estado) => {
                                    const isCurrent = (!activo.asignadoA && activo.estado === estado);
                                    return (
                                        <Menu.Item key={estado}>
                                            {({ active }) => (
                                                <button
                                                    onClick={() => handleUpdateEstado(estado)}
                                                    className={`${active ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
                                                        } group flex items-center w-full px-4 py-2.5 text-sm font-medium transition-colors rounded-lg`}
                                                >
                                                    <div className={`w-2 h-2 rounded-full mr-3 ${estado === 'Disponible' ? 'bg-blue-500' :
                                                        estado === 'Activo' ? 'bg-green-500' :
                                                            estado === 'En Mantenimiento' ? 'bg-yellow-500' :
                                                                'bg-red-500'
                                                        }`} />
                                                    <span className="flex-1 text-left">{estado}</span>
                                                    {isCurrent && <CheckCircle className="h-4 w-4 ml-2 text-blue-500" />}
                                                </button>
                                            )}
                                        </Menu.Item>
                                    );
                                })}
                            </div>
                        </Menu.Items>
                    </Transition>
                </Menu>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
                <div className="overflow-x-auto scrollbar-hide">
                    <nav className="-mb-px flex space-x-8 min-w-max px-4">
                        {(() => {
                            const categoriaLower = activo?.categoria?.toLowerCase() || '';
                            const esEquipo = ['equipo', 'equipos', 'notebook', 'notebooks', 'laptop', 'laptops', 'computador', 'computadora', 'pc'].includes(categoriaLower);
                            return [
                                { id: 'info', label: 'Información', icon: HardDrive },
                                { id: 'software', label: 'Software & Seguridad', icon: Shield, show: esEquipo },
                                { id: 'conexion', label: 'Conexión Remota', icon: Monitor, show: esEquipo },
                                { id: 'asignacion', label: 'Asignación e Historial', icon: User, show: true }
                            ];
                        })().filter(tab => tab.show !== false).map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                    }`}
                            >
                                <tab.icon className="h-4 w-4 flex-shrink-0" />
                                <span className="hidden sm:inline">{tab.label}</span>
                                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Tab Content */}
            <div className="mt-6">
                {activeTab === 'info' && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                            {/* Información General */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                    <HardDrive className="h-5 w-5 mr-2 text-blue-600" />
                                    Información General
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex items-center space-x-3">
                                        <HardDrive className="h-4 w-4 text-gray-400" />
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Código</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{activo.codigo}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <Activity className="h-4 w-4 text-gray-400" />
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Categoría</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{activo.categoria}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <MapPin className="h-4 w-4 text-gray-400" />
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Ubicación</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{formatUbicacion(activo.ubicacion)}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <DollarSign className="h-4 w-4 text-gray-400" />
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Valor</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{activo.valor != null ? `$${Number(activo.valor).toLocaleString('es-CL')}` : 'Sin valor'}</p>
                                        </div>
                                    </div>
                                    {activo.nombreEquipo && (
                                        <div className="flex items-center space-x-3">
                                            <HardDrive className="h-4 w-4 text-gray-400" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Nombre del Equipo</p>
                                                <p className="font-medium text-gray-900 dark:text-white">{activo.nombreEquipo}</p>
                                            </div>
                                        </div>
                                    )}
                                    {activo.tipoEquipo && (
                                        <div className="flex items-center space-x-3">
                                            <HardDrive className="h-4 w-4 text-gray-400" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Tipo de Equipo</p>
                                                <p className="font-medium text-gray-900 dark:text-white">{activo.tipoEquipo}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Especificaciones Técnicas */}
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                                    <Cpu className="h-5 w-5 mr-2 text-blue-600" />
                                    Especificaciones Técnicas
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {activo.procesador && (
                                        <div className="flex items-center space-x-3">
                                            <Cpu className="h-4 w-4 text-gray-400" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Procesador</p>
                                                <p className="font-medium text-gray-900 dark:text-white">{activo.procesador}</p>
                                            </div>
                                        </div>
                                    )}
                                    {activo.sistemaOperativo && (
                                        <div className="flex items-center space-x-3">
                                            <Monitor className="h-4 w-4 text-gray-400" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Sistema Operativo</p>
                                                <p className="font-medium text-gray-900 dark:text-white">{activo.sistemaOperativo}</p>
                                            </div>
                                        </div>
                                    )}
                                    {activo.ram && (
                                        <div className="flex items-center space-x-3">
                                            <Activity className="h-4 w-4 text-gray-400" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">RAM</p>
                                                <p className="font-medium text-gray-900 dark:text-white">{activo.ram}</p>
                                            </div>
                                        </div>
                                    )}
                                    {(activo.almacenamiento || activo.capacidad || activo.discosJson) && (
                                        <div className="flex items-center space-x-3">
                                            <HardDrive className="h-4 w-4 text-gray-400" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Almacenamiento</p>
                                                <p className="font-medium text-gray-900 dark:text-white">
                                                    {formatStorageDisplay(activo.almacenamiento, activo.capacidad, activo.discosJson)}
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                    {activo.marca && (
                                        <div className="flex items-center space-x-3">
                                            <Building className="h-4 w-4 text-gray-400" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Marca</p>
                                                <p className="font-medium text-gray-900 dark:text-white">{activo.marca}</p>
                                            </div>
                                        </div>
                                    )}
                                    {activo.modelo && (
                                        <div className="flex items-center space-x-3">
                                            <Tag className="h-4 w-4 text-gray-400" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Modelo</p>
                                                <p className="font-medium text-gray-900 dark:text-white">{activo.modelo}</p>
                                            </div>
                                        </div>
                                    )}
                                    {activo.serie && (
                                        <div className="flex items-center space-x-3">
                                            <FileText className="h-4 w-4 text-gray-400" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">N° Serie</p>
                                                <p className="font-medium text-gray-900 dark:text-white">{activo.serie}</p>
                                            </div>
                                        </div>
                                    )}
                                    {/* Campos para dispositivos móviles */}
                                    {activo.numeroCelular && (
                                        <div className="flex items-center space-x-3">
                                            <Phone className="h-4 w-4 text-gray-400" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Número Celular</p>
                                                <p className="font-medium text-gray-900 dark:text-white">{activo.numeroCelular}</p>
                                            </div>
                                        </div>
                                    )}
                                    {activo.imei && (
                                        <div className="flex items-center space-x-3">
                                            <Smartphone className="h-4 w-4 text-gray-400" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">IMEI</p>
                                                <p className="font-medium text-gray-900 dark:text-white">{activo.imei}</p>
                                            </div>
                                        </div>
                                    )}
                                    {/* Capacidad se maneja dentro de almacenamiento ya que en móviles a veces se usa indistintamente */}
                                    {activo.pulgadas && (
                                        <div className="flex items-center space-x-3">
                                            <Monitor className="h-4 w-4 text-gray-400" />
                                            <div>
                                                <p className="text-sm text-gray-500 dark:text-gray-400">Tamaño de Pantalla</p>
                                                <p className="font-medium text-gray-900 dark:text-white">{activo.pulgadas}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Usuario Asignado */}
                        {activo.asignadoA && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                                        <User className="h-5 w-5 mr-2 text-blue-600" />
                                        Usuario Asignado
                                    </h3>
                                    <button
                                        onClick={() => setActiveTab('asignacion')}
                                        className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                    >
                                        Ver historial
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="flex items-center space-x-3">
                                        <User className="h-4 w-4 text-gray-400" />
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Nombre Completo</p>
                                            <button
                                                onClick={() => navigate(`/usuarios/${activo.asignadoA.id}`)}
                                                className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline text-left"
                                                title="Ver ficha del usuario"
                                            >
                                                {activo.asignadoA.nombre} {activo.asignadoA.apellido}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <Mail className="h-4 w-4 text-gray-400" />
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{activo.asignadoA.email}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                        <Building className="h-4 w-4 text-gray-400" />
                                        <div>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Departamento</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{activo.asignadoA.departamento}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Observaciones */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:col-span-2 group">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                                    <FileText className="h-5 w-5 mr-2 text-blue-600" />
                                    Observaciones
                                </h3>
                                {!isEditingObservaciones && (
                                    <button
                                        onClick={() => {
                                            setEditObservacionesText(activo.observaciones || '')
                                            setIsEditingObservaciones(true)
                                        }}
                                        className="text-gray-400 hover:text-blue-600 transition-colors opacity-0 group-hover:opacity-100 p-1"
                                        title="Editar Observaciones"
                                    >
                                        <Edit2 className="h-4 w-4" />
                                    </button>
                                )}
                            </div>

                            {isEditingObservaciones ? (
                                <div className="space-y-3">
                                    <textarea
                                        className="w-full text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px]"
                                        value={editObservacionesText}
                                        onChange={(e) => setEditObservacionesText(e.target.value)}
                                        placeholder="Ingrese observaciones..."
                                    />
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            onClick={() => setIsEditingObservaciones(false)}
                                            className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors flex items-center"
                                        >
                                            <X className="h-4 w-4 mr-1" />
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleSaveObservaciones}
                                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors flex items-center shadow-sm"
                                        >
                                            <Check className="h-4 w-4 mr-1" />
                                            Guardar
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                    {activo.observaciones || <span className="text-gray-400 italic">Sin observaciones registradas.</span>}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'asignacion' && (
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                                        <User className="h-5 w-5 mr-2 text-blue-600" />
                                        {activo.asignadoA ? 'Usuario Asignado' : 'Asignación de Activo'}
                                    </h3>
                                    <div className="flex space-x-2">
                                        {!activo.asignadoA ? (
                                            <button
                                                onClick={() => setShowAsignarModal(true)}
                                                className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm w-full sm:w-auto"
                                            >
                                                <Plus className="h-4 w-4" />
                                                <span>Asignar Activo</span>
                                            </button>
                                        ) : (
                                            <button
                                                onClick={handleQuitarAsignacion}
                                                className="flex items-center justify-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm w-full sm:w-auto"
                                            >
                                                <X className="h-4 w-4" />
                                                <span>Quitar Asignación</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 sm:p-6">
                                {activo.asignadoA ? (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                                        {/* Información del Usuario */}
                                        <div className="space-y-4">
                                            <div className="flex items-center space-x-3">
                                                <User className="h-4 w-4 text-gray-400" />
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Nombre Completo</p>
                                                    <button
                                                        onClick={() => navigate(`/usuarios/${activo.asignadoA.id}`)}
                                                        className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline text-left"
                                                        title="Ver ficha del usuario"
                                                    >
                                                        {activo.asignadoA.nombre} {activo.asignadoA.apellido}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                <Mail className="h-4 w-4 text-gray-400" />
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Email</p>
                                                    <p className="font-medium text-gray-900 dark:text-white">{activo.asignadoA.email}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                <Building className="h-4 w-4 text-gray-400" />
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Departamento</p>
                                                    <p className="font-medium text-gray-900 dark:text-white">{activo.asignadoA.departamento}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Información de Asignación */}
                                        <div className="space-y-4">
                                            <div className="flex items-center space-x-3">
                                                <Calendar className="h-4 w-4 text-gray-400" />
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Fecha de Asignación</p>
                                                    <p className="font-medium text-gray-900 dark:text-white">
                                                        {formatDate(activo.asignadoA.fechaAsignacion)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                <Activity className="h-4 w-4 text-gray-400" />
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Estado de Asignación</p>
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(activo.asignadoA.estado || 'Activa')}`}>
                                                        {activo.asignadoA.estado || 'Activa'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <User className="mx-auto h-12 w-12 text-gray-400" />
                                        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Activo sin asignar</h3>
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                            Este activo no está asignado a ningún usuario. Haz clic en "Asignar Activo" para asignarlo.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Historial de Asignaciones integrado */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                                    <History className="h-5 w-5 mr-2 text-blue-600" />
                                    Historial de Asignaciones
                                </h3>
                            </div>
                            <div className="p-4 sm:p-6">
                                <HistorialAsignacionesContent activo={activo} />
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'software' && (
                    <>
                        <VicariusActivoPanel activoId={activo.id} />
                        <IntuneBitLockerPanel activoId={activo.id} onEstado={setEstadoIntune} />
                        <SoftwareSecurityManager activoId={activo.id} activoData={activo} ocultarSoftwareYSeguridad ocultarBitLocker={estadoIntune === 'ok'} />
                    </>
                )}

                {activeTab === 'conexion' && (
                    <RemoteConnectionManager
                        activoData={activo}
                        onDataUpdated={fetchActivo}
                    />
                )}
            </div>

            {/* Modal de Asignación */}
            {
                activo && (
                    <AsignarActivoModal
                        isOpen={showAsignarModal}
                        onClose={() => setShowAsignarModal(false)}
                        activo={activo}
                        onAsignacionCreada={handleAsignacionCreada}
                    />
                )
            }

            {editando && (
                <ActivosForm modal edit idProp={activo.codigo} onClose={() => setEditando(false)} onSaved={(r) => (r?.codigo && r.codigo !== codigo ? navigate(`/activos/${r.codigo}`, { replace: true }) : fetchActivo())} />
            )}
        </div >
    )
}
